import {
    AppScreen, Section, SectionHeader, Bleed, CardGrid,
    Button, Badge, TimeBadge,
    AvatarStack, Person,
    CardRail, PlaceCard, MeetingCard,
    InverseBanner, InverseTime,
} from 'components/ds';
import styles from './HomePreview.module.css';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

/**
 * 홈 화면 미리보기 — /ds/home
 *
 * 현재 pages/home/index.tsx (2,789줄) 의 실제 섹션 구성을 그대로 두고
 * 새 디자인 시스템으로만 다시 그린 것입니다. 데이터는 목업입니다.
 *
 * 실제 홈의 섹션 순서 (pages/home/index.tsx 기준):
 *   GreetingSection → sectionToday → InviteCodeButton → DailyFeatureBanner
 *   → sectionParticipating → sectionUnsettledFinished → sectionUpcoming
 *   → sectionRegular → sectionVote → sectionNeedsPrep → MiniGamesSection
 *   → sectionTrending → sectionSuggestion → BottomNavigation
 * 여기서도 같은 순서를 지킵니다. 없는 기능은 만들어 넣지 않았습니다.
 *
 * 사진 출처
 *  - 모임 카드: 모임 자체에는 대표 이미지가 없습니다. 실제로는
 *    MeetingResponse.place.externalPlaceId 로 placePhotos API
 *    (/dokocrew/api/v1/travel/photos) 를 호출해 장소 사진을 받아 씁니다.
 *    SubPlaceModal·PlaceExplore 가 이미 쓰는 방식입니다.
 *  - 추천 카드: pages/home/index.tsx 70~88행에 이미 있는 테마 이미지 URL 그대로.
 */

const LABELS = { going: '참여', pending: '미정', declined: '불참' };

const P: Person[] = [
    { userNo: 1, name: '재권', attendance: 'going', isHost: true },
    { userNo: 2, name: '민서', attendance: 'going' },
    { userNo: 3, name: '지훈', attendance: 'pending' },
    { userNo: 4, name: '수아', attendance: 'declined' },
    { userNo: 5, name: '태윤', attendance: 'going' },
];

/** home/index.tsx 에 이미 들어 있는 테마 이미지 URL (Unsplash) */
const IMG = {
    bar: 'https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=640&h=360&fit=crop',
    beach: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400&h=400&fit=crop',
    running: 'https://images.unsplash.com/photo-1541625602330-2277a4c46182?w=400&h=400&fit=crop',
    dinner: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=400&fit=crop',
    picnic: 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=400&h=400&fit=crop',
    // 주의: 홈의 autumnT 테마가 쓰는 photo-1507003211169 은 단풍이 아니라 인물 사진입니다.
    //       (pages/home/index.tsx 86행 — 실제 앱에도 그대로 들어가 있는 값)
    //       미리보기에서는 japanAutumnT 가 쓰는 실제 단풍 사진으로 대체했습니다.
    autumn: 'https://images.unsplash.com/photo-1545569341-9eb8b30979d9?w=400&h=400&fit=crop',
    hike: 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=400&h=400&fit=crop',
    wine: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=400&h=250&fit=crop',
    brunch: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=400&h=250&fit=crop',
    cafe: 'https://images.unsplash.com/photo-1522383225653-ed111181a951?w=400&h=400&fit=crop',
};

const IcPinS = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" /><circle cx="12" cy="10" r="3" />
    </svg>
);

export const HomePreview = () => {
    // 홈은 탭 화면 — 상단바 없이 하단 GNB 만. 라우팅·i18n 은 AppScreen 이 처리합니다.
    return (
        <AppScreen bar="none" gnb chatDot>
            {/* ── GreetingSection — copyTone 이 시간대·요일로 만드는 인사말 ── */}
            <Section spacing="tight">
                <Bleed>
                    <InverseBanner
                        title="불금이에요, 재권님!"
                        sub={<>가장 가까운 약속은 <InverseTime>2시간 후</InverseTime> · 합정 소금집</>}
                    />
                </Bleed>
            </Section>

            {/* ── 오늘 예정하는 모임 (sectionToday) — 홈의 주인공 카드 ── */}
            <Section spacing="tight">
                <SectionHeader title="오늘 예정하는 모임" gap={false} />
                <div className={cls("hp-gap")} />
                <article className={cls("hp-hero")}>
                    {/* 장소 사진 — placePhotos API 로 받아온 이미지 */}
                    <div className={cls("hp-hero-cover")}>
                        <img src={IMG.bar} alt="" />
                        <span className={cls("hp-hero-time")}><TimeBadge urgent>2시간 후</TimeBadge></span>
                    </div>

                    <div className={cls("hp-hero-body")}>
                        <span className={cls("hp-hero-label")}>곧 모임</span>
                        <h3 className={cls("hp-hero-title")}>금요일 저녁 번개</h3>

                        <div className={cls("hp-hero-tags")}>
                            <Badge tone="neutral">당일 모임</Badge>
                            <Badge tone="weak">주최자</Badge>
                        </div>

                        <div className={cls("hp-place")}>
                            <span className={cls("hp-place-icon")}><IcPinS /></span>
                            <span className={cls("hp-place-text")}>
                                <span className={cls("hp-place-name")}>소금집 델리</span>
                                <span className={cls("hp-place-region")}>서울 마포구 월드컵로 12 · 1.2km</span>
                            </span>
                            <span className={cls("hp-place-arrow")} aria-hidden>›</span>
                        </div>

                        <div className={cls("hp-hero-foot")}>
                            <AvatarStack people={P} size="sm" labels={LABELS} />
                            <span className={cls("hp-members-count")}>5명 중 3명 참여</span>
                        </div>

                        <div className={cls("hp-hero-actions")}>
                            <Button variant="brand" size="md">출발 알리기</Button>
                            <Button variant="secondary" size="md">위치 공유</Button>
                            <Button variant="ghost" size="md">채팅</Button>
                        </div>
                    </div>
                </article>
            </Section>

            {/* ── 초대 코드 입력 (InviteCodeButton) ──
                회색 스트립을 화면 끝까지 빼서 아래 섹션 구분 띠와 폭을 맞춥니다.
                이 스트립 자체가 구분 역할을 하므로 다음 섹션은 divided 를 쓰지 않습니다. */}
            <Section spacing="tight">
                <Bleed>
                    <button type="button" className={cls("hp-invite")}>
                        초대 코드가 있나요?
                        <span className={cls("hp-invite-cta")}>코드 입력</span>
                    </button>
                </Bleed>
            </Section>

            {/* ── 참여 중 (sectionParticipating) ── */}
            <Section spacing="tight">
                <SectionHeader title="참여 중" count={2} moreLabel="전체보기" onMore={() => undefined} />
                <CardGrid>
                    <MeetingCard
                        name="제주 3박 4일"
                        when="9월 20일 (토) ~ 9월 23일 (화)"
                        where="제주특별자치도 제주시"
                        time={<TimeBadge size="sm">D-19</TimeBadge>}
                        imageUrl={IMG.beach}
                        badges={[{ label: '여행', tone: 'lime' }]}
                        people={<AvatarStack people={P.slice(0, 4)} size="sm" labels={LABELS} />}
                    />
                    <MeetingCard
                        name="수요일 러닝크루"
                        when="9월 10일 (수) 오후 8시"
                        where="한강공원 망원지구"
                        time={<TimeBadge size="sm">D-9</TimeBadge>}
                        imageUrl={IMG.running}
                        badges={[{ label: '정기 모임', tone: 'neutral' }]}
                        people={<AvatarStack people={P.slice(1, 4)} size="sm" labels={LABELS} />}
                    />
                </CardGrid>
            </Section>

            {/* ── 정산 대기 중 (sectionUnsettledFinished) ── */}
            <Section divided>
                <SectionHeader
                    title="정산 대기 중"
                    count={1}
                    sub="끝난 모임의 정산이 아직 남아 있어요"
                />
                <CardGrid>
                <MeetingCard
                    name="8월 마지막 회식"
                    when="8월 29일 (금)"
                    where="을지로 골뱅이집"
                    time={<TimeBadge size="sm" past>지난 모임</TimeBadge>}
                    imageUrl={IMG.dinner}
                    badges={[{ label: '내가 받을 돈 42,000원', tone: 'danger' }]}
                    people={<AvatarStack people={P} size="sm" labels={LABELS} />}
                />
                </CardGrid>
            </Section>

            {/* ── 다가올 모임 (sectionUpcoming) ── */}
            <Section divided>
                <SectionHeader title="다가올 모임" count={3} moreLabel="전체보기" onMore={() => undefined} />
                <CardGrid>
                    <MeetingCard
                        name="추석 가족 모임"
                        when="10월 5일 (일) 오후 12시"
                        where="용인 큰집"
                        time={<TimeBadge size="sm">D-34</TimeBadge>}
                        imageUrl={IMG.picnic}
                        people={<AvatarStack people={P.slice(0, 3)} size="sm" labels={LABELS} />}
                    />
                </CardGrid>
            </Section>

            {/* ── 일정 투표 진행 중 (sectionVote) ── */}
            <Section divided>
                <SectionHeader
                    title="일정 투표 진행 중"
                    count={1}
                    sub="아직 투표하지 않은 모임이 있어요"
                />
                <CardGrid>
                <MeetingCard
                    name="동아리 가을 MT"
                    when="후보 3개 · 6명 중 4명 투표함"
                    where="가평 펜션 (예정)"
                    time={<TimeBadge size="sm" urgent>내일 마감</TimeBadge>}
                    imageUrl={IMG.cafe}
                    badges={[{ label: '내 투표 필요', tone: 'brand' }]}
                />
                </CardGrid>
            </Section>

            {/* ── 준비 필요해요 (sectionNeedsPrep) — 장소가 없으니 사진도 없습니다 ── */}
            <Section divided>
                <SectionHeader title="준비 필요해요" sub="장소나 시간이 아직 안 정해진 모임" />
                <CardGrid>
                <MeetingCard
                    name="신입 환영회"
                    when="날짜 미정"
                    where="장소 미정"
                    badges={[{ label: '장소 미정', tone: 'outline' }, { label: '일정 미정', tone: 'outline' }]}
                />
                </CardGrid>
            </Section>

            {/* ── 자주 했던 모임 (sectionTrending) ── */}
            <Section divided>
                <SectionHeader title="자주 했던 모임" sub="같은 사람들과 또 만나보세요" />
                <CardRail>
                    <PlaceCard name="수요일 러닝크루" meta="12회 · 5명" imageUrl={IMG.running} />
                    <PlaceCard name="주말 등산" meta="8회 · 4명" imageUrl={IMG.hike} />
                    <PlaceCard name="보드게임 모임" meta="6회 · 6명" imageUrl={IMG.brunch} />
                </CardRail>
            </Section>

            {/* ── 이런 모임 어때요? (sectionSuggestion) ── */}
            <Section divided last>
                <SectionHeader title="이런 모임 어때요?" sub="9월 첫째 주에 어울리는 모임" />
                <CardRail>
                    <PlaceCard name="가을 단풍 나들이" meta="추천" imageUrl={IMG.autumn} badge={{ label: 'NEW' }} wide />
                    <PlaceCard name="연말 송년회" meta="추천" imageUrl={IMG.wine} wide />
                    <PlaceCard name="주말 브런치" meta="추천" imageUrl={IMG.brunch} wide />
                </CardRail>
            </Section>

        </AppScreen>
    );
};
